import { IClientClaim, IJwtPayload } from '@lwt-common/authentication/build/Authentication';
import { Auth } from '@lwt-common/authentication';
import { sendError } from './helpers';

export const appId = 'hsb';
export const leadsPlusAppId = 'lp';
export const helixBoostController = appId;

export const leadsPlusDomain = 'leadsplus';
export const helixLeadsPlusController = leadsPlusDomain;

export const getTokenFromDOM = (): string => {
    const domNode = document.getElementById('headerLinksJson');
    if (domNode) {
        const domData = JSON.parse(domNode.innerHTML);
        return domData?.lwaToken ?? '';
    }
    return '';
};

export const getJwtPayload = (rawToken: string | null): IJwtPayload | undefined => {
    return Auth.parseToken(rawToken);
};

export const hasAccessToApplication = (
    tokenJwtPayload: IJwtPayload | undefined | null | false,
    applicationCode: string
): boolean => {
    if (!tokenJwtPayload) {
        return false;
    }
    const claim = tokenJwtPayload.getLwtClaim();
    if (!claim) {
        return false;
    }
    const clients = claim.getClients();

    if (!clients || clients.length < 1) {
        return false;
    }

    const clientsThatHaveContacts = clients.filter((client) => {
        const applications = client.getApplications();
        const codes = applications.getCodes();
        return codes.includes(applicationCode);
    });

    return clientsThatHaveContacts && clientsThatHaveContacts.length > 0;
};

export const getUserClient = (): null | IClientClaim => {
    const token = getTokenFromDOM();
    if (!token) {
        return null;
    }
    const payload = getJwtPayload(token);
    if (!payload) {
        return null;
    }
    const claim = payload.getLwtClaim();
    if (!claim) {
        return null;
    }
    const clients = claim.getClients();

    if (!clients || clients.length < 1) {
        return null;
    }

    const userClients = clients.filter((client) => {
        return client.isUserClient();
    });

    if (!userClients || userClients.length > 1) {
        // sentry error
        sendError(new Error(`Why does this user(${claim.getUserId}) have multiple userClients?`));
        return null;
    }

    return userClients[0];
}; // client type is user

export const getFirstName = (tokenJwtPayload: IJwtPayload | undefined | null | false): string => {
    if (!tokenJwtPayload) {
        return '';
    }
    return tokenJwtPayload.getFirstName() ?? '';
};

export const getLastName = (tokenJwtPayload: IJwtPayload | undefined | null | false): string => {
    if (!tokenJwtPayload) {
        return '';
    }
    return tokenJwtPayload.getLastName() ?? '';
};

// appears as if the default photo will be a square with the agents initials...
export const getPictureUrl = (tokenJwtPayload: IJwtPayload | undefined | null | false): string => {
    if (!tokenJwtPayload) {
        return '';
    }
    return tokenJwtPayload.getPicture() ?? '';
};

export const getEmail = (tokenJwtPayload: IJwtPayload | undefined | null | false): string => {
    if (!tokenJwtPayload) {
        return '';
    }
    return tokenJwtPayload.getEmail() ?? '';
};

export const hasAccessToContacts = (): boolean => {
    const token = getTokenFromDOM();
    if (!token) {
        return false;
    }

    const payload = getJwtPayload(token);
    if (!payload) {
        return false;
    }

    return hasAccessToApplication(payload, 'con');
};

// LW_ENV is set per build in webpack.{dev,dev.hot,staging,prod}.js. Don't use NODE_ENV here:
// webpack replaces it with the build mode, and staging builds in production mode.
const isLwStaging = (): boolean => process.env.LW_ENV === 'stg';

const getLwEnvironmentString = (): string => {
    return isLwStaging() ? 'stg.' : '';
};

export const getLwDomain = (): string => {
    return `${getLwEnvironmentString()}lwolf.com`;
};

export const getPlatformDomain = (): string => {
    return `https://platform.${getLwEnvironmentString()}lwolf.com`;
};

export const getContactsDomain = (): string => {
    return `https://crm.api.${getLwEnvironmentString()}lwolf.com/v1`;
};

export const getClientSwitcherEnvironmentString = (): 'stg' | 'prod' | 'pre' | 'dev' => {
  return isLwStaging() ? 'stg' : 'prod';
};
